import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Check, Sparkles, Loader2, ShieldCheck, ArrowLeft, X, Eye, MousePointer, DollarSign } from "lucide-react";
import { Button } from "./components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Feature bullets shown under each plan card. Kept per-tier so the caps land
// exactly where the pricing page needs them — Starter is honest about the fair-
// use limits (Feb 2026), Pro sells "unlimited" as the upgrade hook.
const FEATURES_BY_TIER = {
  starter: [
    "Unlimited connected channels",
    "Per-video sales attribution",
    "Universal pixel + UTM links + short links",
    "AI Hook Analysis — 250 competitor videos / mo",
    "Blend Hooks with AI — 60 generations / mo",
    "Ask AI (Claude Haiku) — 200 messages / mo",
    "Public Hook Teardown share pages",
    "Real-time dashboard with deltas",
  ],
  pro: [
    "Everything in Starter, plus:",
    "Unlimited AI Hook Analysis",
    "Unlimited Blend Hooks",
    "Ask AI upgraded to Claude Sonnet 4.5 (deeper reasoning)",
    "1,000 Ask AI messages / mo (5× Starter)",
    "Priority support + early access to new AI features",
  ],
};

const FEATURES = FEATURES_BY_TIER.starter; // legacy fallback for any old imports

export function PricingScreen({ billing, onClose, onPurchased }) {
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/billing/plans`);
        setPlans(data);
      } catch {
        toast.error("Couldn't load plans");
      }
    })();
  }, []);

  // Detect Stripe redirect back: /?billing=success&session_id=...
  // Polls /billing/checkout/status until `payment_status === 'paid'` (or 8 tries).
  // We memoise with `useCallback` so the recursive `setTimeout` chain doesn't
  // capture a stale `onPurchased` prop across renders.
  const cleanQuery = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete("billing");
    url.searchParams.delete("session_id");
    window.history.replaceState({}, "", url.toString());
  }, []);

  const pollCheckoutStatus = useCallback(async (sid, attempt) => {
    if (attempt >= 8) {
      toast.message("We're still finalizing your subscription — refresh in a moment.");
      cleanQuery();
      return;
    }
    try {
      const { data } = await axios.get(`${API}/billing/checkout/status/${sid}`);
      if (data.payment_status === "paid") {
        toast.success(`Welcome to ${data.plan_id?.toUpperCase()}! VidROI unlocked.`);
        cleanQuery();
        onPurchased?.();
        return;
      }
      if (data.status === "expired") {
        toast.error("Checkout expired — please try again.");
        cleanQuery();
        return;
      }
      setTimeout(() => pollCheckoutStatus(sid, attempt + 1), 1500);
    } catch {
      setTimeout(() => pollCheckoutStatus(sid, attempt + 1), 1500);
    }
  }, [cleanQuery, onPurchased]);

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const status = qs.get("billing");
    const sid = qs.get("session_id");
    if (status === "canceled") {
      toast.info("Checkout canceled — no charge made.");
      const url = new URL(window.location.href);
      url.searchParams.delete("billing");
      window.history.replaceState({}, "", url.toString());
    } else if (status === "success" && sid) {
      pollCheckoutStatus(sid, 0);
    }
  }, [pollCheckoutStatus]);

  /**
   * Read the Partnero affiliate cookie set by Partnero's Universal JS.
   *
   * When a visitor lands on the site via an affiliate link (e.g.
   * vidroi.io/?partnero=ab12cd), Partnero's universal-js script drops a
   * `partnero_partner` cookie with the partner's key. We forward that key
   * to our backend, which sets it as Stripe's `client_reference_id` on the
   * checkout session — Partnero's Stripe webhook reads it and attributes
   * the commission. Cookie lifetime is controlled by Partnero (60d default).
   */
  const readPartneroPartner = () => {
    try {
      const m = document.cookie.match(/(?:^|;\s*)partnero_partner=([^;]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    } catch (_) {
      return null;
    }
  };

  const subscribe = async (planId, trialDays = null) => {
    setLoading(`${planId}${trialDays ? `-trial` : ""}`);
    try {
      const payload = {
        plan_id: planId,
        origin_url: window.location.origin,
      };
      if (trialDays) payload.trial_days = trialDays;
      const partnerKey = readPartneroPartner();
      if (partnerKey) payload.partner_key = partnerKey;
      const { data } = await axios.post(`${API}/billing/checkout`, payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Couldn't start checkout");
      toast.error(msg);
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 noise relative overflow-hidden" data-testid="pricing-screen">
      <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-red-600/20 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-red-500/10 blur-[140px] pointer-events-none" />

      <header className="relative max-w-5xl mx-auto px-6 pt-6 flex items-center justify-between">
        {onClose ? (
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white"
            data-testid="pricing-close"
          >
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
        ) : <span />}
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">VidROI · Pricing</div>
      </header>

      <main className="relative max-w-5xl mx-auto px-6 py-12 sm:py-16">
        <div className="text-center mb-12 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[11px] uppercase tracking-[0.2em] font-semibold mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            Track every dollar a video drives
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05] mb-4">
            One price.
            <br />
            <span className="text-red-500 drop-shadow-[0_0_24px_rgba(255,0,0,0.55)]">
              Every feature
            </span> included.
          </h1>
          <p className="text-zinc-400 max-w-xl mx-auto text-[15px] leading-relaxed">
            Pick the plan that matches how many offers you're promoting on YouTube. The features are identical — only the offer count differs.
          </p>
        </div>

        {!plans ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-red-500" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5 mb-10" data-testid="pricing-tiers">
            {plans.tiers.map((t, i) => (
              <PlanCard
                key={t.id}
                tier={t}
                features={FEATURES}
                current={billing?.plan === t.id}
                highlight={i === 1}
                loading={loading === t.id || loading === `${t.id}-trial`}
                trialEligible={!billing?.trial_used && (billing?.plan === "free" || !billing?.plan)}
                onClick={() => subscribe(t.id)}
                onTrial={() => subscribe(t.id, 7)}
              />
            ))}
          </div>
        )}

        <RoiCalculator />

        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5 flex items-center gap-3 max-w-3xl mx-auto" data-testid="moneyback">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex-shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-white font-medium">30-day money-back guarantee</div>
            <div className="text-sm text-zinc-400">Not seeing the lift you expected in your first month? Reply to your receipt and we'll refund every cent — no questions asked.</div>
          </div>
        </div>
      </main>
    </div>
  );
}

function PlanCard({ tier, features, current, highlight, loading, trialEligible, onClick, onTrial }) {
  return (
    <div
      data-testid={`plan-card-${tier.id}`}
      className={`relative overflow-hidden rounded-2xl border backdrop-blur-xl p-7 sm:p-8 animate-fade-up transition-all
        ${highlight
          ? "bg-gradient-to-br from-red-500/[0.10] to-transparent border-red-500/30 shadow-[0_30px_80px_-30px_rgba(255,0,0,0.45)]"
          : "bg-white/[0.02] border-white/[0.08]"
        }`}
    >
      {highlight && (
        <div className="absolute top-4 right-4 px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-300 text-[10px] uppercase tracking-[0.18em] font-semibold">
          Most popular
        </div>
      )}
      <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">{tier.name}</div>
      <div className="flex items-baseline gap-1.5 mb-1.5">
        <span className="text-5xl font-bold tracking-tight text-white tabular-nums">${tier.price_usd.toFixed(0)}</span>
        <span className="text-zinc-500 text-sm">/ month</span>
      </div>
      <div className="text-zinc-400 text-sm mb-6">{tier.blurb}</div>

      {trialEligible && !current ? (
        <div className="flex flex-col gap-2">
          <Button
            onClick={onTrial}
            disabled={loading}
            className={`w-full h-11 font-medium ${
              highlight
                ? "bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_20px_rgba(255,0,0,0.35)]"
                : "bg-white text-zinc-900 hover:bg-zinc-100"
            }`}
            data-testid={`trial-${tier.id}-btn`}
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</> : `Start 14-day free trial`}
          </Button>
          <button
            type="button"
            onClick={onClick}
            disabled={loading}
            className="text-xs text-zinc-500 hover:text-zinc-300 underline underline-offset-2 transition-colors disabled:opacity-50"
            data-testid={`subscribe-${tier.id}-btn`}
          >
            or skip the trial and subscribe now
          </button>
        </div>
      ) : (
        <Button
          onClick={onClick}
          disabled={loading || current}
          className={`w-full h-11 font-medium ${
            current
              ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/15"
              : highlight
                ? "bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_20px_rgba(255,0,0,0.35)]"
                : "bg-white text-zinc-900 hover:bg-zinc-100"
          }`}
          data-testid={`subscribe-${tier.id}-btn`}
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</> : current ? <><Check className="w-4 h-4 mr-1.5" /> Current plan</> : `Start ${tier.name}`}
        </Button>
      )}

      <ul className="space-y-2.5 mt-7">
        {trialEligible && !current && (
          <li className="flex items-start gap-2.5 text-sm text-emerald-300 font-medium" data-testid={`trial-line-${tier.id}`}>
            <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span>14 days free, then ${tier.price_usd}/mo — cancel anytime</span>
          </li>
        )}
        <li className="flex items-start gap-2.5 text-sm text-zinc-200 font-medium">
          <Check className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <span>{tier.offer_limit} offer{tier.offer_limit !== 1 ? "s" : ""} tracked</span>
        </li>
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
            <Check className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// =============================================================================
// RoiCalculator — quick "is this worth it" demo
// "Track 100k views/mo at $0.50 EPC = $50k of revenue you'll attribute for $29/mo"
// Three sliders: monthly_views, ctr%, epc — output: revenue attributed + ROI multiple.
// =============================================================================
function RoiCalculator() {
  const [views, setViews] = useState(100000);
  const [ctrPct, setCtrPct] = useState(2.5);
  const [epc, setEpc] = useState(0.5);

  const { clicks, revenue, monthlyPrice, roiMultiple } = useMemo(() => {
    const c = Math.round(views * (ctrPct / 100));
    const r = c * epc;
    const price = 29; // Show ROI vs the cheapest tier — most compelling number
    const mult = price > 0 ? r / price : 0;
    return { clicks: c, revenue: r, monthlyPrice: price, roiMultiple: mult };
  }, [views, ctrPct, epc]);

  return (
    <div
      data-testid="roi-calculator"
      className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-br from-white/[0.04] to-transparent p-6 sm:p-8 max-w-3xl mx-auto mb-6 animate-fade-up"
    >
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-red-500/10 blur-[100px] pointer-events-none" />

      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-red-400" />
          <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 font-semibold">Is VidROI worth it?</span>
        </div>
        <h3 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight mb-1">
          Punch in your numbers.
        </h3>
        <p className="text-sm text-zinc-400 mb-6">
          Move the sliders to match your channel. We'll show the revenue you'll be able to attribute back to specific videos.
        </p>

        <div className="grid sm:grid-cols-3 gap-5 mb-6" data-testid="roi-inputs">
          <Slider
            icon={Eye}
            label="Monthly views"
            value={views}
            min={1000}
            max={5_000_000}
            step={1000}
            format={(v) => formatBigNumber(v)}
            onChange={setViews}
            testid="roi-views"
          />
          <Slider
            icon={MousePointer}
            label="Click-through to your link"
            suffix="%"
            value={ctrPct}
            min={0.1}
            max={20}
            step={0.1}
            format={(v) => v.toFixed(1) + "%"}
            onChange={setCtrPct}
            testid="roi-ctr"
          />
          <Slider
            icon={DollarSign}
            label="Earnings per click"
            prefix="$"
            value={epc}
            min={0.05}
            max={20}
            step={0.05}
            format={(v) => "$" + v.toFixed(2)}
            onChange={setEpc}
            testid="roi-epc"
          />
        </div>

        <div className="grid grid-cols-3 gap-3 sm:gap-4 pt-5 border-t border-white/[0.06]" data-testid="roi-outputs">
          <Stat label="Clicks / mo" value={formatBigNumber(clicks)} muted />
          <Stat label="Revenue you'll attribute" value={"$" + formatBigNumber(revenue)} highlight testid="roi-revenue" />
          <Stat label="ROI vs $29/mo" value={`${roiMultiple.toFixed(0)}×`} testid="roi-multiple" />
        </div>

        <p className="text-xs text-zinc-500 mt-4 max-w-xl leading-relaxed">
          That's <span className="text-zinc-300">${formatBigNumber(revenue)}</span> of revenue you'll know — to the video — for <span className="text-zinc-300">${monthlyPrice}/mo</span>. <span className="text-zinc-600">Industry-typical YouTube CTR is 1–3% and EPC ranges from $0.20 (low-ticket) to $5+ (info products).</span>
        </p>
      </div>
    </div>
  );
}

function Slider({ icon: Icon, label, value, min, max, step, prefix = "", suffix = "", format, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">
          <Icon className="w-3.5 h-3.5" />
          <span>{label}</span>
        </div>
      </div>
      <div className="text-2xl font-semibold tabular-nums text-white mb-2" data-testid={`${testid}-value`}>
        {format ? format(value) : `${prefix}${value}${suffix}`}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 bg-white/[0.06] rounded-full appearance-none cursor-pointer accent-red-500 outline-none"
        data-testid={`${testid}-slider`}
        style={{
          background: `linear-gradient(to right, rgb(239 68 68) 0%, rgb(239 68 68) ${
            ((value - min) / (max - min)) * 100
          }%, rgba(255,255,255,0.06) ${((value - min) / (max - min)) * 100}%, rgba(255,255,255,0.06) 100%)`,
        }}
      />
    </div>
  );
}

function Stat({ label, value, highlight, muted, testid }) {
  return (
    <div data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold mb-1 ${muted ? "text-zinc-600" : "text-zinc-500"}`}>{label}</div>
      <div
        className={`text-2xl sm:text-3xl font-bold tabular-nums tracking-tight ${
          highlight
            ? "text-red-400 drop-shadow-[0_0_24px_rgba(255,0,0,0.5)]"
            : muted
              ? "text-zinc-400"
              : "text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function formatBigNumber(n) {
  if (!isFinite(n)) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1) + "K";
  if (n >= 100) return Math.round(n).toString();
  return n.toFixed(n < 10 ? 2 : 0);
}

// =============================================================================
// UpgradeGate — inline call-out shown in place of locked features when the
// user is on the free plan. If they haven't used their free trial yet, the
// primary CTA is "Start free trial" (one-click to Stripe with trial_days=14),
// with "See plans" demoted to a secondary text link. That way every paywall
// in the app becomes an action to start the trial, not just a dead-end wall.
export function UpgradeGate({ feature, onUpgrade, onTrial, trialEligible }) {
  return (
    <div
      className="rounded-2xl border border-dashed border-red-500/30 bg-red-500/[0.04] p-7 text-center animate-fade-up"
      data-testid="upgrade-gate"
    >
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-400 mb-4">
        <Sparkles className="w-5 h-5" />
      </div>
      <div className="text-white text-lg font-semibold mb-1">
        {feature || "Subscribe to unlock"}
      </div>
      <div className="text-sm text-zinc-400 max-w-md mx-auto mb-5">
        {trialEligible
          ? <>Try VidROI free for 14 days — <span className="text-white font-medium">$0 today</span>, from $29/mo after. Cancel anytime.</>
          : <>VidROI starts at <span className="text-white font-medium">$29/mo</span> Starter, or <span className="text-white font-medium">$79/mo</span> Pro for unlimited AI. 30-day money back.</>
        }
      </div>
      {trialEligible && onTrial ? (
        <div className="flex flex-col items-center gap-2">
          <Button
            onClick={onTrial}
            className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_18px_rgba(255,0,0,0.35)]"
            data-testid="upgrade-gate-trial-cta"
          >
            Start 14-day free trial
          </Button>
          <button
            type="button"
            onClick={onUpgrade}
            className="text-xs text-zinc-500 hover:text-zinc-300 underline underline-offset-2 transition-colors"
            data-testid="upgrade-gate-cta"
          >
            or see plans
          </button>
        </div>
      ) : (
        <Button
          onClick={onUpgrade}
          className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_18px_rgba(255,0,0,0.35)]"
          data-testid="upgrade-gate-cta"
        >
          See plans
        </Button>
      )}
    </div>
  );
}

// Compact billing card shown in the sidebar footer (above user block).
export function BillingBadge({ billing, onUpgrade, onManage }) {
  if (!billing) return null;
  const isPaid = billing.plan !== "free";
  return (
    <div
      className="mx-3 mb-3 rounded-xl border p-3 bg-white/[0.02] border-white/10"
      data-testid="billing-badge"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Plan</div>
        <div className={`text-[11px] font-semibold ${isPaid ? "text-red-400" : "text-zinc-500"}`}>
          {billing.plan_name}
        </div>
      </div>
      {isPaid ? (
        <>
          <div className="text-xs text-zinc-400 mb-2.5" data-testid="billing-badge-offers">
            {billing.offer_count} / {billing.offer_limit} offers used
          </div>
          <button
            onClick={onManage}
            className="w-full text-[11px] uppercase tracking-[0.15em] text-zinc-400 hover:text-white py-1.5 rounded-md border border-white/10 hover:border-white/30 transition-colors"
            data-testid="manage-billing-btn"
          >
            Manage billing
          </button>
        </>
      ) : (
        <button
          onClick={onUpgrade}
          className="w-full text-[11px] uppercase tracking-[0.15em] text-red-300 hover:text-white py-1.5 rounded-md bg-red-500/10 border border-red-500/25 hover:bg-red-500/20 transition-colors"
          data-testid="sidebar-upgrade-btn"
        >
          Upgrade →
        </button>
      )}
    </div>
  );
}
